import { getAt } from '@finn-no/mobility-item-insertion-shared';
import { Select } from '@warp-ds/react';
import React from 'react';
import { Controller, type FieldError, type UseControllerProps, useFormContext } from 'react-hook-form';
import type { SelectWidgetModel, ValueOption } from '../../../types/widget.js';

interface SelectWidgetProps extends Omit<SelectWidgetModel, 'type'> {
  values: Array<ValueOption>;
}

export const SelectWidget = React.forwardRef<HTMLSelectElement, SelectWidgetProps>(
  ({ mandatory, helpText, values, name, ...rest }, ref) => {
    const children = [<option key="" value="" />].concat(
      values?.map((option: ValueOption) => (
        <option key={option.id} value={option.id}>
          {option.label}
        </option>
      )) ?? [],
    );

    return (
      <Select {...rest} hint={helpText} optional={!mandatory} always ref={ref} data-testid={name} data-attribute={name}>
        {children}
      </Select>
    );
  },
);

// TODO: Move this "Controlled" part out - it seems to be a pattern that is repeated in all widgets
export const ControlledSelectWidget = ({ name, helpText, rules, ...props }: SelectWidgetProps & UseControllerProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const error = getAt<FieldError>(errors, name);
  if (error) {
    helpText = (error.message as string) ?? 'Validation error';
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const { onChange, value, ...rest } = field;
        return (
          <SelectWidget
            title={props.label}
            helpText={helpText}
            invalid={!!error}
            defaultValue={props.defaultValue}
            label={props.label}
            values={props.values}
            mandatory={props.mandatory}
            aria-required={props.mandatory}
            className={props.className}
            {...rest}
            value={value ?? ''}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              const value = e.target.value;
              if (!!value && !Number.isNaN(Number.parseInt(value, 10))) {
                onChange(Number.parseInt(e.target.value, 10));
              } else {
                onChange(e.target.value);
              }
            }}
          />
        );
      }}
      rules={rules}
      shouldUnregister={true}
    />
  );
};
